import ImageIcon from '@mui/icons-material/Image';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Dispatch, ReactNode, SetStateAction } from 'react';
import type React from 'react';

const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

const isValidFileTypeForAcceptedTypes = (file: { type: string }, acceptedFileTypes: 'pdf' | 'image'): boolean => {
	if (acceptedFileTypes === 'pdf') {
		return file.type === 'application/pdf';
	} else {
		return ACCEPTED_IMAGE_TYPES.includes(file.type);
	}
};

export type FileUploadEntry = {
	id: string;
	file: File;
};

export type SimpleFileUploadProps<Multiple extends boolean | undefined> = {
	multiple?: Multiple;
	onChange: Dispatch<SetStateAction<Multiple extends true ? FileUploadEntry[] : File | null>>;
	acceptedFileTypes?: 'pdf' | 'image';
	maxFileSize?: number; // in bytes
	/** Disables drag-and-drop, file selection, and clipboard paste. */
	disabled?: boolean;
};

// One overload for each possible value of `multiple` -- that way, TypeScript can infer different types for `props` for
// the caller based on whether or not `multiple` is provided. The `undefined` case must go first.
export function SimpleFileUploader(props: SimpleFileUploadProps<undefined>): ReactNode;
export function SimpleFileUploader(props: SimpleFileUploadProps<true>): ReactNode;
export function SimpleFileUploader(props: SimpleFileUploadProps<false>): ReactNode;
export function SimpleFileUploader({
	multiple = false,
	onChange,
	acceptedFileTypes = 'pdf',
	maxFileSize = DEFAULT_MAX_FILE_SIZE,
	disabled = false,
}: SimpleFileUploadProps<boolean | undefined>) {
	const [isDragOver, setIsDragOver] = useState(false);
	const [isInvalidDrag, setIsInvalidDrag] = useState(false);
	const dropzoneRef = useRef<HTMLDivElement>(null);

	const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		// Prevent default even when disabled, so the browser never falls back to
		// opening a dropped file; only the upload state updates are skipped.
		event.preventDefault();
		if (disabled) {
			return;
		}
		setIsDragOver(true);

		const items = Array.from(event.dataTransfer.items).filter((item) => item.kind === 'file');
		setIsInvalidDrag(!isValidDragFiles(multiple, items, acceptedFileTypes));
	};

	const handleDragLeave = () => {
		setIsDragOver(false);
		setIsInvalidDrag(false);
	};

	const validateAndSetFiles = useCallback(
		(newFiles: File[]) => {
			const isValidFileType = (file: { type: string }) => {
				if (acceptedFileTypes === 'pdf') {
					return file.type === 'application/pdf';
				} else {
					return ACCEPTED_IMAGE_TYPES.includes(file.type);
				}
			};

			const isValidFileSize = (file: { size: number }) => {
				return file.size <= maxFileSize;
			};

			const validSizeFiles = newFiles.filter((file) => isValidFileSize(file));
			if (validSizeFiles.length === 0 || !isValidDragFiles(multiple, validSizeFiles, acceptedFileTypes)) {
				return;
			}

			// After validation, filter again to get only the files that passed both type and size validation
			const validFiles = validSizeFiles.filter((file) => isValidFileType(file));

			const newEntries = validFiles.map((file) => ({ id: nanoid(), file }));
			onChange((prev) => {
				if (Array.isArray(prev)) {
					return [...prev, ...newEntries];
				} else {
					return newEntries[0]?.file ?? prev;
				}
			});
		},
		[multiple, acceptedFileTypes, maxFileSize, onChange],
	);

	const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragOver(false);
		setIsInvalidDrag(false);
		if (disabled) {
			return;
		}

		const files = Array.from(event.dataTransfer.items)
			.filter((item) => item.kind === 'file')
			.map((item) => item.getAsFile())
			.filter((f) => f !== null);

		validateAndSetFiles(files);
	};

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.files) {
			return;
		}

		validateAndSetFiles(Array.from(event.target.files));
	};

	const handlePaste = useCallback(
		(event: ClipboardEvent) => {
			if (disabled || acceptedFileTypes !== 'image') {
				return;
			}

			const items = event.clipboardData?.items;
			if (!items) {
				return;
			}

			const files: File[] = [];
			for (const item of items) {
				if (item.kind === 'file' && item.type.startsWith('image/')) {
					const file = item.getAsFile();
					if (file) {
						files.push(file);
					}
				}
			}

			if (files.length > 0) {
				event.preventDefault();
				validateAndSetFiles(files);
			}
		},
		[disabled, acceptedFileTypes, validateAndSetFiles],
	);

	useEffect(() => {
		const handlePasteEvent = (event: ClipboardEvent) => {
			// Only handle paste if the dropzone is in the DOM and visible
			if (dropzoneRef.current && document.contains(dropzoneRef.current)) {
				handlePaste(event);
			}
		};

		window.addEventListener('paste', handlePasteEvent);
		return () => {
			window.removeEventListener('paste', handlePasteEvent);
		};
	}, [acceptedFileTypes, multiple, maxFileSize, handlePaste]);

	const plural = multiple ? 's' : '';
	const fileTypeLabel = acceptedFileTypes === 'pdf' ? 'PDF' : 'image';
	const acceptAttribute = acceptedFileTypes === 'pdf' ? '.pdf,application/pdf' : ACCEPTED_IMAGE_TYPES.join(',');
	const supportedFormats = acceptedFileTypes === 'pdf' ? 'PDF files' : 'PNG, JPEG, GIF, WebP';
	const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));

	return (
		<div
			ref={dropzoneRef}
			className={clsx(
				'grid h-full place-items-center rounded-lg border border-blue-a4 p-6 shadow-[inset_0px_0px_20px_var(--shadow-color)] transition-colors [--shadow-color:var(--color-gray-1)] light:border-blue-a5 light:[--shadow-color:var(--color-gray-a3)]',
				isDragOver ?
					isInvalidDrag ? 'bg-red-9/70'
					:	'bg-blue-a4'
				:	'bg-blue-2/80',
			)}
			onDragEnter={handleDragOver}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			data-testid='file-upload-dropzone'
		>
			<div
				className={clsx(
					'flex flex-col items-center gap-3 transition-[filter,opacity]',
					isDragOver && 'opacity-50 grayscale',
				)}
			>
				{acceptedFileTypes === 'pdf' ?
					<UploadFileIcon className='text-[2rem] text-blue-9 drop-shadow-[2px_3px_0px_var(--color-gray-a5)]' />
				:	<ImageIcon className='text-[2rem] text-blue-9 drop-shadow-[2px_3px_0px_var(--color-gray-a5)]' />}
				<p className='text-xl font-medium text-gray-a12' data-testid='drag-text'>
					Drag {fileTypeLabel} file{plural} here
				</p>
				<p className='text-gray-a9'>or</p>
				<div>
					<Button
						component='label'
						variant='outlined'
						htmlFor='file-upload'
						disabled={disabled}
						data-testid='file-select-button'
					>
						<p>
							Click to select {fileTypeLabel} file{plural}
						</p>
					</Button>
					<input
						type='file'
						id='file-upload'
						multiple={multiple}
						accept={acceptAttribute}
						className='hidden'
						disabled={disabled}
						onChange={handleInputChange}
						data-testid='file-input'
					/>
				</div>
				<p className='text-xs text-gray-a9'>
					Supports {supportedFormats} (max {maxSizeMB}MB)
					{acceptedFileTypes === 'image' && ' • Paste from clipboard (Ctrl/Cmd+V)'}
				</p>
			</div>
		</div>
	);
}

/** True if all files are valid type _and_ count is allowed by `multiple`. */
const isValidDragFiles = (
	multiple: boolean,
	files: Array<{ type: string }>,
	acceptedFileTypes: 'pdf' | 'image',
): boolean => {
	if (files.length === 0) {
		return true; // Some browsers might conceal the list of files until the drop event, so we can't say for sure it's invalid
	}

	if (!multiple && files.length !== 1) {
		return false;
	}

	// Validate that all files are of the correct type
	return files.every((file) => isValidFileTypeForAcceptedTypes(file, acceptedFileTypes));
};
